import { createTheme } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { colors } from './variables';

export const createAppTheme = (mode: PaletteMode) => createTheme({
  palette: {
    mode,
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
      contrastText: colors.primary.contrastText,
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
      contrastText: colors.secondary.contrastText,
    },
    background: mode === 'dark' ? {
      default: colors.background.default,
      paper: colors.background.paper,
    } : {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    error: {
      main: colors.status.error.main,
    },
    warning: {
      main: colors.status.warning.main,
    },
    success: {
      main: colors.status.success.main,
    },
    info: {
      main: colors.status.info.main,
    },
    text: mode === 'dark' ? {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    } : {
      primary: '#1a1a1a',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '3.5rem',
      fontWeight: 800,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Scrollbar styles
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: mode === 'dark'
              ? 'rgba(255, 255, 255, 0.3) rgba(0, 0, 0, 0.2)'
              : 'rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.05)',
          },
          '*::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '*::-webkit-scrollbar-track': {
            background: mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: '5px',
          },
          '*::-webkit-scrollbar-thumb': {
            background: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
            borderRadius: '5px',
            border: mode === 'dark'
              ? '2px solid rgba(0, 0, 0, 0.2)'
              : '2px solid rgba(0, 0, 0, 0.05)',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '1rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(0, 240, 255, 0.3)',
          },
        },
        contained: {
          background: colors.gradients.primary,
          boxShadow: colors.shadows.primary.sm,
          '&:hover': {
            background: colors.gradients.primary,
            boxShadow: colors.shadows.primary.md,
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: colors.primary.alpha[8],
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: mode === 'dark' ? colors.background.card : '#ffffff',
          backdropFilter: mode === 'dark' ? colors.backdrop.blur20 : 'none',
          border: mode === 'dark' ? `1px solid ${colors.border.default}` : '1px solid #e0e0e0',
          borderRadius: 16,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          background: mode === 'dark' ? colors.background.overlay : '#ffffff',
          backdropFilter: mode === 'dark' ? colors.backdrop.blur10 : 'none',
          border: mode === 'dark' ? `1px solid ${colors.border.light}` : '1px solid #e0e0e0',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: mode === 'dark' ? colors.background.overlay : '#ffffff',
          backdropFilter: mode === 'dark' ? colors.backdrop.blur20 : 'none',
          borderBottom: mode === 'dark' ? `1px solid ${colors.border.default}` : '1px solid #e0e0e0',
          boxShadow: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            background: mode === 'dark' ? colors.white.alpha[3] : '#f9f9f9',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: mode === 'dark' ? colors.white.alpha[5] : '#f0f0f0',
              '& fieldset': {
                borderColor: colors.primary.alpha[50],
              },
            },
            '&.Mui-focused': {
              background: mode === 'dark' ? colors.white.alpha[5] : '#ffffff',
              '& fieldset': {
                borderColor: colors.primary.main,
                borderWidth: 2,
              },
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          background: mode === 'dark' ? colors.primary.alpha[10] : '#f5f5f5',
          border: mode === 'dark'
            ? `1px solid ${colors.border.primary}`
            : '1px solid #e0e0e0',
          color: mode === 'dark' ? colors.text.primary : '#374151',
          '& .MuiChip-icon': {
            color: mode === 'dark' ? colors.primary.light : '#6B7280',
          },
        },
        outlined: {
          background: mode === 'dark' ? 'transparent' : '#ffffff',
          border: mode === 'dark'
            ? `1px solid ${colors.border.primary}`
            : '1px solid #d1d5db',
        },
      },
    },
  },
});

// Export default dark theme for backwards compatibility
export const theme = createAppTheme('dark');